import React, { useEffect, useRef, useState } from 'react'
import { AutoSizer, Column, defaultTableRowRenderer, SortDirection, SortDirectionType, Table } from 'react-virtualized'
import makeStyles from '@mui/styles/makeStyles'
import 'react-virtualized/styles.css'
import { useTranslation } from 'react-i18next'
import {
  CATEGORY,
  CURRENCY,
  DATA_FROM,
  EXPERIENCE,
  HORIZONTE,
  HORIZONT_VALUE,
  ISIN,
  LOSS_ABILITY,
  MARKET,
  MOVEMENT,
  NAME,
  PREFERENCE,
  PRICE,
  RISK,
  RISK_VALUE,
  TICKER,
} from '../../constants/textConstants'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { Security, SecurityPrices } from '../../redux/reducers/securities'
import cs from 'apexcharts/dist/locales/cs.json'
import { TableRowProps } from 'react-virtualized/dist/es/Table'
import { ButtonBase, Typography } from '@mui/material'
import ReactApexChart from 'react-apexcharts'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { useInstrumentApi } from '../../hooks/useInstrumentApi'
import { useSelector } from '../../redux/hooks/useSelector'
// @ts-ignore
import styleCss from './VirtualizedTableBody.css'
import { INSTRUMENT_INPUT_DIALOG_ID, ORDER_INPUT_DIALOG_ID } from '../../constants/dialogConstants'
import { ColoredIconButton } from '../common/ColoredIconButton'
import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import AddIcon from '@mui/icons-material/AddRounded'
import { instrumentStyles } from '../../utils/instrumentIconStyles'
import { tokens } from '../../styles/tokens'
import useMobileWidth from '../../hooks/useMobileWidth'
import { formatNumber } from '../../utils/numbersFormatter'
import { muiTheme } from '../../muiTheme'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardRounded'
import { EditRounded as Edit, ContentCopyRounded as Duplicate } from '@mui/icons-material'

const useStyles = makeStyles(theme => ({
  tableRowBig: {
    display: 'flex',
    flexDirection: 'row',
    height: '150px',
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
  textColor: {
    // color: theme.palette.primary.dark,
    color: 'black',
  },
  boldText: {
    fontWeight: 'bold',
  },
  separatorLeftSmall: {
    marginLeft: theme.spacing(1.5),
  },
  separator: {
    flexGrow: 1,
  },
  separatorLeft: {
    marginLeft: theme.spacing(1),
  },
  separatorLeftHuge: {
    marginLeft: theme.spacing(10),
  },
  separatorLeftLarge: {
    marginLeft: theme.spacing(5),
  },
  borderColorShare: {
    // borderColor: theme.palette.error.main,
    borderColor: 'red',
  },
  borderColorFcMarket: {
    borderColor: 'white',
    // borderColor: theme.palette.success.main,
  },
  borderColorBond: {
    borderColor: 'white',
    // borderColor: theme.palette.primary.main,
  },
  borderColorOther: {
    borderColor: 'white',
    // borderColor: theme.palette.grey[600],
  },
  textColorGreen: {
    // color: theme.palette.success.main,
    color: 'green',
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '500px',
    minWidth: '200px',
  },
  instrumentTableButton: {
    height: '15px',
    width: '15px',
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(0.2),
  },
  buttonIcon: {
    height: '12px',
    width: '12px',
  },
  iconSize: {
    height: '20px',
    width: '30px',
    lineHeight: '15px',
    fontSize: '8px',
    marginTop: theme.spacing(0.2),
  },
  market: {
    width: '100px',
  },
  // Bunka s akcemi radku (upravit + duplikovat) - tlacitka vedle sebe.
  actionCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    '& .MuiIconButton-root': {
      marginRight: 0,
    },
  },
  chartCard: {
    margin: '8px 16px 16px',
    padding: theme.spacing(2),
    background: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    borderRadius: tokens.radius.lg,
    boxShadow: tokens.shadow.sm,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  chartHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  chartTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    margin: 0,
    lineHeight: 1.2,
  },
  chartSubtitle: {
    fontSize: '0.75rem',
    color: tokens.color.neutral[400],
    margin: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  chartPrice: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    margin: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  chartControls: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  rangeTrack: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    padding: '3px',
    backgroundColor: tokens.color.neutral[100],
    borderRadius: '12px',
  },
  rangeButton: {
    padding: '6px 12px',
    borderRadius: '9px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    color: tokens.color.neutral[500],
    transition: `background-color ${tokens.transition.fast}, color ${tokens.transition.fast}`,
    '&:hover': {
      color: tokens.color.neutral[900],
      backgroundColor: 'rgba(255, 255, 255, 0.55)',
    },
  },
  rangeSelected: {
    backgroundColor: tokens.color.white,
    color: tokens.color.neutral[900],
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(8, 38, 69, 0.12), 0 1px 1px rgba(8, 38, 69, 0.06)',
    '&:hover': {
      backgroundColor: tokens.color.white,
    },
  },
  downloadButton: {
    width: '34px',
    height: '34px',
    borderRadius: '10px',
    color: tokens.color.neutral[500],
    backgroundColor: tokens.color.neutral[100],
    transition: `background-color ${tokens.transition.fast}, color ${tokens.transition.fast}`,
    '&:hover': {
      color: tokens.color.neutral[900],
      backgroundColor: tokens.color.neutral[200],
    },
  },
}))

const CHART_RANGES = ['1M', '3M', '6M', 'YTD', '1R'] as const

const rangeToMinDate = (range: string): number | undefined => {
  const now = new Date()
  switch (range) {
    case '1M':
      return new Date(new Date().setMonth(now.getMonth() - 1)).getTime()
    case '3M':
      return new Date(new Date().setMonth(now.getMonth() - 3)).getTime()
    case '6M':
      return new Date(new Date().setMonth(now.getMonth() - 6)).getTime()
    case 'YTD':
      return new Date(now.getFullYear(), 0, 1).getTime()
    case '1R':
      return new Date(new Date().setFullYear(now.getFullYear() - 1)).getTime()
    default:
      return undefined
  }
}

interface InstrumentTypeRendererProps {
  rowData: Security
}

const InstrumentTypeRenderer = ({ rowData }: InstrumentTypeRendererProps) => {
  return (
    <div
      style={{
        // @ts-ignore
        ...instrumentStyles(muiTheme)[`iconType${rowData.assetClass || 'E'}`],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: muiTheme.palette.secondary.main,
        fontWeight: 600,
        letterSpacing: '0.03em',
        height: '18px',
        width: '36px',
        lineHeight: '18px',
        fontSize: '9px',
        borderRadius: '6px',
      }}
    >
      {rowData.assetClass}
    </div>
  )
}

const LastPriceIndicationRenderer = ({ rowData }: InstrumentTypeRendererProps) => {
  // @ts-ignore
  if (!rowData.lastPriceIndication) {
    return null
  }

  // @ts-ignore
  if (rowData.lastPriceIndication > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
        <ArrowUpwardIcon style={{ color: tokens.color.success[700], fontSize: 13 }} />
        <Typography style={{ color: tokens.color.success[700], fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          +{rowData.lastPriceIndication}%
        </Typography>
      </div>
    )
  }
  // @ts-ignore
  else if (rowData.lastPriceIndication < 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
        <ArrowDownwardIcon style={{ color: tokens.color.error[500], fontSize: 13 }} />
        <Typography style={{ color: tokens.color.error[500], fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {rowData.lastPriceIndication}%
        </Typography>
      </div>
    )
  }
  return null
}

interface Props {
  list: Security[]
  setIsUpdate: (params: any) => any
  setIsDuplicate: (params: any) => any
}

// Radi hodnoty ruznych typu (retezce, cisla, null) pro razeni sloupcu instrumentu.
const compareValues = (a: any, b: any) => {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
  return a > b ? 1 : a < b ? -1 : 0
}

const VirtualizedTableBody = ({ list, setIsUpdate, setIsDuplicate }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.ASC)
  const tableRef = useRef()
  const classes = useStyles()
  const isMobile = useMobileWidth()
  const { getSecurityPrices } = useInstrumentApi()
  const dispatch = useDispatch()
  const { getSecurity } = useInstrumentApi()

  const sortedList = React.useMemo(() => {
    if (!sortBy) return list
    const sorted = [...list].sort((a, b) => compareValues((a as any)[sortBy], (b as any)[sortBy]))
    return sortDirection === SortDirection.DESC ? sorted.reverse() : sorted
  }, [list, sortBy, sortDirection])

  const handleSort = ({ sortBy, sortDirection }: { sortBy: string; sortDirection: SortDirectionType }) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection)
    setSelectedIndex(-1)
  }

  // @ts-ignore
  const securityPrices = useSelector<SecurityPrices>(state => state.securities.securityPrices)

  interface NumberProps {
    index: number
  }

  const _getRowHeight = ({ index }: NumberProps) => (index === selectedIndex ? 540 : 50)
  const rowGetter = ({ index }: NumberProps) => sortedList[index]

  interface RowProps {
    rowIndex: number
    cellData?: any
  }

  const cellRenderer2 = ({ rowIndex }: RowProps) => {
    return (
      <div className={classes.actionCell}>
        <ColoredIconButton
          className={classes.instrumentTableButton}
          size="small"
          title={t('UPDATE_SECURITY')}
          icon={<Edit fontSize="small" className={classes.buttonIcon} />}
          onClick={() => {
            setIsDuplicate(false)
            setIsUpdate(true)
            getSecurity(sortedList[rowIndex].securityId)
            dispatch(openDialog(INSTRUMENT_INPUT_DIALOG_ID))
          }}
        />
        {/* Duplikace: nacte stejny instrument do dialogu, ale ulozi se jako novy
            zaznam (typicky se meni jen trh). */}
        <ColoredIconButton
          className={classes.instrumentTableButton}
          size="small"
          title={t('DUPLICATE_SECURITY')}
          icon={<Duplicate fontSize="small" className={classes.buttonIcon} />}
          onClick={() => {
            setIsUpdate(false)
            setIsDuplicate(true)
            getSecurity(sortedList[rowIndex].securityId)
            dispatch(openDialog(INSTRUMENT_INPUT_DIALOG_ID))
          }}
        />
      </div>
    )
  }

  const cellRendererWithTranslation = ({ cellData }: RowProps) => {
    if (cellData == null) {
      return ''
    } else {
      return t(String(cellData))
    }
  }

  useEffect(() => {
    if (sortedList[selectedIndex] && sortedList[selectedIndex].securityId) {
      getSecurityPrices(sortedList[selectedIndex].securityId)
    }
    if (tableRef && tableRef.current) {
      // @ts-ignore
      tableRef.current.recomputeRowHeights()
    }
  }, [selectedIndex])

  useEffect(() => {
    if (securityPrices) {
      setSeriesGraphLine([
        {
          // @ts-ignore
          name: 'Tržní cena',
          // @ts-ignore
          data: securityPrices.prices.map((a: any) => {
            return [new Date(a.date).getTime(), a.amount]
          }),
        },
      ])
    }
  }, [securityPrices])

  const [chartRange, setChartRange] = useState('MAX')

  const [optionsGraphLine, setOptionsGraphLine] = React.useState<ApexOptions>({
    chart: {
      id: 'instrument-price-chart',
      height: 380,
      fontFamily: 'inherit',
      zoom: {
        autoScaleYaxis: true,
      },
      toolbar: {
        show: false,
      },
      locales: [cs], //or multi language like [fa, en]
      defaultLocale: 'cs',
    },
    grid: {
      show: true,
      borderColor: tokens.color.neutral[100],
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    noData: {
      text: 'Žádná data k zobrazení',
      style: {
        color: tokens.color.neutral[400],
        fontSize: '13px',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      type: 'datetime',
      min: new Date(new Date().getFullYear(), 0, 1).getTime(),
      tickAmount: 6,
      labels: {
        style: {
          colors: '#6A7280',
          fontSize: '11px',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val ? formatNumber(val) : ''
        },
        style: {
          colors: '#6A7280',
          fontSize: '11px',
        },
      },
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy',
      },
      y: {
        formatter: (val: number) => (val == null ? '' : formatNumber(val)),
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        colorStops: [
          { offset: 0, color: '#11457F', opacity: 0.28 },
          { offset: 100, color: '#11457F', opacity: 0.02 },
        ],
      },
    },
    markers: {
      size: 0,
      strokeColors: tokens.color.white,
      hover: {
        size: 5,
      },
    },
    stroke: {
      show: true,
      curve: 'smooth' as const,
      width: 2.5,
      colors: [tokens.color.primary[600]],
    },
    legend: {
      show: false,
      position: 'bottom',
      horizontalAlign: 'center',
    },
  })

  useEffect(() => {
    setChartRange('MAX')
  }, [selectedIndex])

  useEffect(() => {
    setOptionsGraphLine(prev => ({
      ...prev,
      xaxis: {
        ...(prev.xaxis as any),
        min: rangeToMinDate(chartRange),
        max: undefined,
      },
    }))
  }, [chartRange, selectedIndex])

  const downloadChart = async () => {
    // @ts-ignore
    const res = await ApexCharts.exec('instrument-price-chart', 'dataURI')
    if (!res || !res.imgURI) return
    const link = document.createElement('a')
    link.href = res.imgURI
    link.download = `${sortedList[selectedIndex]?.ticker || sortedList[selectedIndex]?.isin || 'graf'}.png`
    link.click()
  }
  const [seriesGraphLine, setSeriesGraphLine] = React.useState([
    {
      data: [],
    },
  ])

  function addMonths(date: Date, months: number) {
    let d = date.getDate()
    date.setMonth(date.getMonth() + +months)
    if (date.getDate() != d) {
      date.setDate(0)
    }
    return date
  }

  useEffect(() => {
    setSeriesGraphLine([
      {
        // @ts-ignore
        type: 'area',
        name: 'Tržní cena',
        // @ts-ignore
        data: [],
      },
    ])
  }, [selectedIndex])

  const rowRenderer = (props: TableRowProps) => {
    const { index, style, className, key, rowData } = props
    if (index === selectedIndex) {
      const security: Security = rowData
      return (
        <div style={{ ...style, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }} className={className} key={key}>
          {defaultTableRowRenderer({
            ...props,
            style: { width: style.width, height: 50 },
          })}

          <div className={classes.chartCard} onClick={e => e.stopPropagation()}>
            <div className={classes.chartHeader}>
              <div>
                <p className={classes.chartTitle}>{security?.name}</p>
                <p className={classes.chartSubtitle}>
                  {[security?.ticker, security?.isin, security?.market].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div>
                <p className={classes.chartPrice}>
                  {security?.lastQuotePrice != null ? `${formatNumber(security.lastQuotePrice)} ${security?.currency || ''}` : ''}
                </p>
                <LastPriceIndicationRenderer rowData={security} />
              </div>
              <div className={classes.chartControls}>
                <div className={classes.rangeTrack}>
                  {[...CHART_RANGES, 'MAX'].map(range => (
                    <ButtonBase
                      key={range}
                      disableRipple
                      className={`${classes.rangeButton} ${chartRange === range ? classes.rangeSelected : ''}`}
                      onClick={() => setChartRange(range)}
                    >
                      {range === 'MAX' ? t('ALL') : range}
                    </ButtonBase>
                  ))}
                </div>
                <ButtonBase disableRipple className={classes.downloadButton} onClick={downloadChart} title="Stáhnout graf (PNG)">
                  <DownloadRoundedIcon style={{ fontSize: 18 }} />
                </ButtonBase>
              </div>
            </div>
            <ReactApexChart options={optionsGraphLine} series={seriesGraphLine} type="area" height={380} width="100%" />
          </div>
        </div>
      )
    }
    return defaultTableRowRenderer({
      // @ts-ignore
      ...props,
      style: { ...style, width: style.width, height: '38px' },
    })
  }

  const _noRowsRenderer = () => {
    return <div className={styleCss.noRows}>No rows</div>
  }

  const _rowClassName = ({ index }: NumberProps) => {
    if (index < 0) {
      return styleCss.headerRow
    } else {
      return index % 2 === 0 ? styleCss.evenRow : styleCss.oddRow
    }
  }

  const { t } = useTranslation()

  return (
    <div style={{ height: '90vh', width: '100%' }}>
      <AutoSizer>
        {({ width, height }) => (
          <Table
            headerHeight={38}
            height={height}
            noRowsRenderer={_noRowsRenderer}
            rowClassName={_rowClassName}
            overscanRowCount={10}
            rowHeight={_getRowHeight}
            rowGetter={rowGetter}
            rowCount={sortedList.length}
            width={width}
            // @ts-ignore
            ref={tableRef}
            rowRenderer={rowRenderer}
            headerClassName={styleCss.headerColumn}
            sort={handleSort}
            sortBy={sortBy ?? undefined}
            sortDirection={sortDirection}
            onRowClick={({ index }: { index: number }) =>
              setSelectedIndex(index === selectedIndex ? -1 : index)
            }
          >
            {isMobile ? (
              [
                <Column
                  key="assetClass"
                  label="Typ"
                  cellDataGetter={({ rowData }) => rowData}
                  // @ts-ignore
                  cellRenderer={InstrumentTypeRenderer}
                  dataKey="assetClass"
                  width={44}
                />,
                <Column
                  key="name"
                  dataKey="name"
                  label={t(NAME)}
                  width={Math.max(120, width - 44 - 90 - 60 - 72)}
                  style={{ fontWeight: 500 }}
                />,
                <Column
                  key="lastQuotePrice"
                  dataKey="lastQuotePrice"
                  label={t(PRICE)}
                  width={90}
                  cellRenderer={({ cellData }) => (cellData == null ? '' : formatNumber(cellData))}
                  style={{ textAlign: 'right', fontWeight: 500 }}
                  headerStyle={{ textAlign: 'right' }}
                />,
                <Column
                  key="lastPriceIndication"
                  label={t(MOVEMENT)}
                  cellDataGetter={({ rowData }) => rowData}
                  // @ts-ignore
                  cellRenderer={LastPriceIndicationRenderer}
                  dataKey="lastPriceIndication"
                  width={60}
                  headerStyle={{ textAlign: 'right' }}
                />,
                <Column
                  key="securityId"
                  cellDataGetter={({ rowData }) => rowData}
                  cellRenderer={cellRenderer2}
                  dataKey="securityId"
                  disableSort
                  width={72}
                />,
              ]
            ) : (
              [
                <Column
                  key="assetClass"
                  label="Typ"
                  cellDataGetter={({ rowData }) => rowData}
                  // @ts-ignore
                  cellRenderer={InstrumentTypeRenderer}
                  dataKey="assetClass"
                  flexGrow={1}
                  width={50}
                />,
                <Column key="name" dataKey="name" label={t(NAME)} flexGrow={1} width={300} style={{ fontWeight: 500 }} />,
                <Column key="isin" dataKey="isin" label={t(ISIN)} flexGrow={1} width={150} />,
                <Column key="market" dataKey="market" label={t(MARKET)} flexGrow={1} width={75} />,
                <Column key="currency" dataKey="currency" label={t(CURRENCY)} flexGrow={1} width={75} />,
                <Column key="ticker" dataKey="ticker" label={t(TICKER)} flexGrow={1} width={75} />,
                <Column
                  key="category"
                  dataKey="category"
                  label={t(CATEGORY)}
                  flexGrow={1}
                  cellRenderer={cellRendererWithTranslation}
                  width={100}
                />,
                <Column
                  key="skillAndExperience"
                  dataKey="skillAndExperience"
                  label={t(EXPERIENCE)}
                  flexGrow={1}
                  cellRenderer={cellRendererWithTranslation}
                  width={100}
                />,
                <Column
                  key="preference"
                  dataKey="preference"
                  label={t(PREFERENCE)}
                  flexGrow={1}
                  cellRenderer={cellRendererWithTranslation}
                  width={100}
                />,
                <Column
                  key="lossAbility"
                  dataKey="lossAbility"
                  label={t(LOSS_ABILITY)}
                  flexGrow={1}
                  cellRenderer={cellRendererWithTranslation}
                  width={125}
                />,
                <Column
                  key="lastQuotePrice"
                  dataKey="lastQuotePrice"
                  label={t(PRICE)}
                  flexGrow={1}
                  width={75}
                  cellRenderer={({ cellData }) => (cellData == null ? '' : formatNumber(cellData))}
                  style={{ textAlign: 'right', fontWeight: 500 }}
                  headerStyle={{ textAlign: 'right' }}
                />,
                <Column
                  key="lastPriceIndication"
                  label={t(MOVEMENT)}
                  cellDataGetter={({ rowData }) => rowData}
                  // @ts-ignore
                  cellRenderer={LastPriceIndicationRenderer}
                  dataKey="lastPriceIndication"
                  flexGrow={1}
                  width={75}
                  headerStyle={{ textAlign: 'right' }}
                />,
                <Column key="lastQuoteDate" dataKey="lastQuoteDate" label={t(DATA_FROM)} flexGrow={1} width={125} />,
                <Column
                  key="riskValue"
                  dataKey="riskValue"
                  label={t(RISK)}
                  flexGrow={1}
                  width={70}
                  style={{ textAlign: 'right' }}
                  headerStyle={{ textAlign: 'right' }}
                />,
                <Column
                  key="horizonteValue"
                  dataKey="horizonteValue"
                  label={t(HORIZONTE)}
                  flexGrow={1}
                  width={60}
                  style={{ textAlign: 'right' }}
                  headerStyle={{ textAlign: 'right' }}
                />,
                <Column
                  key="securityId"
                  flexGrow={1}
                  cellDataGetter={({ rowData }) => rowData}
                  cellRenderer={cellRenderer2}
                  dataKey="securityId"
                  disableSort
                  width={84}
                />,
              ]
            )}
          </Table>
        )}
      </AutoSizer>
    </div>
  )
}

export default VirtualizedTableBody
